/**
 * V279: 공지사항 본문 뷰어
 *
 * - HTML 본문은 DOMPurify로 정화 후 dangerouslySetInnerHTML 렌더
 * - 평문 본문(legacy)은 whiteSpace: pre-wrap으로 줄바꿈 보존
 * - contentFormat 필드 기준으로 분기 (없으면 legacy plain 으로 간주)
 */

import React, { useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { resolveLinkClick } from '../utils/announcementLink';

export type ContentFormat = 'html' | 'plain';

export interface RichContentViewerProps {
  content: string;
  format?: ContentFormat;
  style?: React.CSSProperties;
  className?: string;
  /**
   * V70G8H: 본문 내 ERP 내부 링크 클릭 시 호출 (SPA 이동 — 라우트 가드가 권한 검사).
   * 없으면 같은 탭 전체 이동. 외부 링크는 항상 새 탭(noopener).
   */
  onInternalLink?: (path: string) => void;
}

/** 공지사항에서 허용하는 태그/속성 (FE/BE 화이트리스트와 동일 정책) */
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'span', 'div',
    'strong', 'em', 'u', 's', 'code',
    'h1', 'h2', 'h3',
    'ul', 'ol', 'li',
    'a',
    'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'hr',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    'src', 'alt', 'title',
    'style', 'class',
    'colspan', 'rowspan',
    'width', 'height', // V279.1: 이미지 리사이즈
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/|data:image\/(?:png|jpeg|gif|webp);base64,)/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
};

export function sanitizeAnnouncementHtml(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG) as unknown as string;
}

// V70 QA 리오픈 N1 재수정 (2026-10-22) — HTML 보존 파이프라인 정규화.
//   이전 수정(line-height 1.75 → 1.7)은 wrapper div 수치만 맞췄고, 실제 renderable paragraph
//   유형별 표시 차이는 미해결 — 사용자 지적 재분석.
//
//   TipTap editor 산출 HTML:
//     - Enter 두 번 (빈 문단) → `<p></p>`
//     - Shift+Enter (hard break) → `<p>abc<br>def</p>`
//     - 일부 브라우저/상황 → `<p><br></p>` (ProseMirror trailing-break 아티팩트가 getHTML 통과)
//   서버 sanitize-html: 기본 설정에서 empty tag 보존, <p>/<br> allowed. 수정 X.
//   FE DOMPurify: KEEP_CONTENT=true 기본, empty tag 보존. 수정 X.
//
//   결과: 저장된 HTML 은 `<p></p>` or `<p><br></p>` 두 형태 혼재 가능.
//   viewer :empty 룰은 `<p></p>` 만 커버 → `<p><br></p>` 는 min-height 미적용 → 높이 축소.
//   정규화: `<p>\s*<br\s*/?>\s*</p>` 패턴을 `<p></p>` 로 치환해 :empty 룰 일관 적용.
function normalizeEmptyParagraphs(html: string): string {
  // self-closing 또는 non-self-closing <br>, 공백/탭/개행 포함, 대소문자 무관.
  return html.replace(/<p>\s*<br\s*\/?\s*>\s*<\/p>/gi, '<p></p>');
}

const RichContentViewer: React.FC<RichContentViewerProps> = ({ content, format, style, className, onInternalLink }) => {
  const isHtml = format === 'html';

  // V70G8H: 본문 링크 클릭 — 내부는 SPA 이동, 외부는 새 탭(noopener), 그 외(무효 href)는 무시
  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented) return;
      const anchor = (e.target as Element | null)?.closest?.('a');
      if (!anchor || !e.currentTarget.contains(anchor)) return;
      const link = resolveLinkClick(anchor.getAttribute('href'), window.location.origin);
      if (link.kind === 'mailto') return;
      // 수정키 클릭(새 탭/창 열기)은 브라우저 기본 동작 — 유효한 http(s)/내부 링크만
      if (link.kind !== 'invalid' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
      e.preventDefault();
      if (link.kind === 'invalid') return;
      if (link.kind === 'external') {
        window.open(link.href, '_blank', 'noopener,noreferrer');
        return;
      }
      if (onInternalLink) onInternalLink(link.path);
      else window.location.assign(link.path);
    },
    [onInternalLink],
  );

  const sanitized = useMemo(() => {
    if (!isHtml) return '';
    // 1) 빈 문단 정규화 (N1 재수정) → 2) DOMPurify 재sanitize.
    //   정규화를 sanitize 전에 수행 — sanitize 는 regex 치환 결과도 재검증하므로 보안 유지.
    const normalized = normalizeEmptyParagraphs(content || '');
    return sanitizeAnnouncementHtml(normalized);
  }, [content, isHtml]);

  if (isHtml) {
    return (
      <>
        {/* V279.2: 표/이미지/제목 등 본문 콘텐츠 렌더링 스타일.
            에디터의 .ProseMirror 스타일과 동일 정책을 .rich-content-viewer 스코프로 복제. */}
        <style>{`
          /* V70 QA 리오픈 N1 재수정 (2026-10-22) — 에디터 ProseMirror 스타일과 1:1 매칭.
              paragraph level line-height 명시 (wrapper 1.7 외 추가 보장). */
          .rich-content-viewer p { margin: 0.4em 0; line-height: 1.7; }
          /* V66 (line-break fix): tiptap 빈 단락(<p></p>)도 1줄 높이를 차지하도록 강제. */
          /* N1 재수정: normalizeEmptyParagraphs 로 <p><br></p> → <p></p> 변환 후 :empty 룰 통일 적용. */
          .rich-content-viewer p:empty { min-height: 1em; }
          .rich-content-viewer p:empty::before { content: "\\200B"; }
          /* N1 재수정 추가 방어: 혹시 정규화를 통과한 <p><br></p> 가 남아도 br-only paragraph 는
              line-break 아티팩트로 보고 1em 높이 보장. (:has 지원 브라우저만 적용 — Chrome 105+/Safari 15.4+). */
          .rich-content-viewer p:has(> br:only-child) { min-height: 1em; }
          .rich-content-viewer h1 { font-size: 1.6em; font-weight: 700; margin: 0.6em 0 0.3em; }
          .rich-content-viewer h2 { font-size: 1.3em; font-weight: 700; margin: 0.6em 0 0.3em; }
          .rich-content-viewer h3 { font-size: 1.1em; font-weight: 700; margin: 0.6em 0 0.3em; }
          .rich-content-viewer ul, .rich-content-viewer ol { padding-left: 1.6em; margin: 0.4em 0; }
          .rich-content-viewer a { color: #2563eb; text-decoration: underline; }
          .rich-content-viewer img { max-width: 100%; height: auto; border-radius: 4px; }
          .rich-content-viewer blockquote {
            border-left: 3px solid #d1d5db;
            padding: 0.2em 0.8em;
            margin: 0.6em 0;
            color: #6b7280;
          }
          .rich-content-viewer hr { border: 0; border-top: 1px solid #e5e7eb; margin: 1em 0; }
          .rich-content-viewer table {
            border-collapse: collapse;
            margin: 0.6em 0;
            width: 100%;
            table-layout: fixed;
          }
          .rich-content-viewer th,
          .rich-content-viewer td {
            border: 1px solid #d1d5db;
            padding: 6px 10px;
            min-width: 60px;
            vertical-align: top;
            word-break: break-word;
          }
          .rich-content-viewer th {
            background: #f3f4f6;
            font-weight: 600;
            text-align: left;
          }
        `}</style>
        <div
          className={`rich-content-viewer ${className || ''}`}
          style={{
            fontSize: '14px',
            // V70 QA 리오픈 N1 (2026-10-22) — RichTextEditor 와 line-height 통일 (1.75 → 1.7).
            //   에디터에서 작성한 간격이 뷰어에서 그대로 보이도록.
            lineHeight: 1.7,
            color: '#374151',
            wordBreak: 'break-word',
            maxWidth: '100%',
            ...style,
          }}
          onClick={handleLinkClick}
          // 1) 저장 시 FE DOMPurify 정화 → 2) 서버 sanitize-html 정화 → 3) 렌더 시 다시 DOMPurify 정화
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      </>
    );
  }

  // legacy: 평문 — whiteSpace pre-wrap 으로 줄바꿈 보존
  return (
    <div
      className={className}
      style={{
        fontSize: '14px',
        lineHeight: 1.75,
        color: '#374151',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxWidth: '100%',
        ...style,
      }}
    >
      {content}
    </div>
  );
};

export default RichContentViewer;
